import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import PostCard from '../components/post/PostCard';
import { Skeleton } from '../components/ui/skeleton';
import { Compass, TrendingUp } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

export default function ExplorePage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [trendingHashtags, setTrendingHashtags] = useState([]);
  const [trendingUsers, setTrendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('posts');

  useEffect(() => {
    const fetchExplore = async () => {
      try {
        const [postsRes, hashtagsRes, usersRes] = await Promise.all([
          api.get('/posts/explore?limit=30'),
          api.get('/trending/hashtags'),
          api.get('/trending/users')
        ]);
        setPosts(postsRes.data);
        setTrendingHashtags(hashtagsRes.data);
        setTrendingUsers(usersRes.data);
      } catch (error) {
        console.error('Error fetching explore:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchExplore();
  }, []);

  const handlePostUpdate = (postId, updates) => {
    setPosts(prev => prev.map(post => 
      post.id === postId ? { ...post, ...updates } : post
    ));
  };

  return (
    <div className="max-w-6xl mx-auto px-4 min-[900px]:ml-60" data-testid="explore-page">
      <div className="py-6">
        <h1 className="font-heading text-2xl font-bold flex items-center gap-2 mb-6">
          <Compass className="w-6 h-6 text-primary" />
          Explore
        </h1>

        <Tabs defaultValue="posts" className="w-full" onValueChange={setActiveTab}>
          <TabsList className="w-full max-w-md bg-muted/50 p-1 rounded-full mb-6">
            <TabsTrigger 
              value="posts" 
              className="flex-1 rounded-full data-[state=active]:bg-primary data-[state=active]:text-white"
              data-testid="explore-posts-tab"
            >
              Posts
            </TabsTrigger>
            <TabsTrigger 
              value="trending" 
              className="flex-1 rounded-full data-[state=active]:bg-primary data-[state=active]:text-white"
              data-testid="explore-trending-tab"
            >
              Trending
            </TabsTrigger>
          </TabsList>

          <TabsContent value="posts">
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Array.from({ length: 12 }).map((_, i) => (
                  <Skeleton key={i} className="aspect-square rounded-2xl" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {posts.map((post) => (
                  <div 
                    key={post.id}
                    className="relative aspect-square rounded-2xl overflow-hidden bg-card border border-border/50 hover:border-primary/50 transition-colors group cursor-pointer"
                  >
                    {post.media_urls?.length > 0 ? (
                      <img 
                        src={post.media_urls[0]} 
                        alt=""
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center p-4 bg-gradient-to-br from-primary/20 to-accent/20">
                        <p className="text-sm line-clamp-4 text-center">{post.content}</p>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute bottom-0 left-0 right-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="flex items-center gap-4 text-white text-sm">
                        <span>❤️ {post.likes_count}</span>
                        <span>💬 {post.comments_count}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="trending">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Trending Hashtags */}
              <div className="glass-card p-6">
                <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Trending Hashtags
                </h3>
                {loading ? (
                  <div className="space-y-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Skeleton key={i} className="h-10 w-full rounded-xl" />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {trendingHashtags.map((tag, index) => (
                      <div 
                        key={tag.hashtag}
                        className="flex items-center justify-between p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-muted-foreground font-mono text-sm">#{index + 1}</span>
                          <span className="font-semibold">#{tag.hashtag}</span>
                        </div>
                        <span className="text-sm text-muted-foreground">{tag.count} posts</span>
                      </div>
                    ))}
                    {trendingHashtags.length === 0 && (
                      <p className="text-muted-foreground text-center py-4">No trending hashtags yet</p>
                    )}
                  </div>
                )}
              </div>

              {/* Trending Users */}
              <div className="glass-card p-6">
                <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-accent" />
                  Popular Creators
                </h3>
                {loading ? (
                  <div className="space-y-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <Skeleton className="w-10 h-10 rounded-full" />
                        <div className="space-y-1">
                          <Skeleton className="h-4 w-24" />
                          <Skeleton className="h-3 w-16" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {trendingUsers.map((tUser, index) => (
                      <a 
                        key={tUser.id}
                        href={`/profile/${tUser.username}`}
                        className="flex items-center justify-between p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-muted-foreground font-mono text-sm">#{index + 1}</span>
                          <img 
                            src={tUser.avatar} 
                            alt={tUser.display_name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                          <div>
                            <p className="font-semibold">{tUser.display_name}</p>
                            <p className="text-sm text-muted-foreground">@{tUser.username}</p>
                          </div>
                        </div>
                        <span className="text-sm text-muted-foreground">{tUser.followers_count} followers</span>
                      </a>
                    ))}
                    {trendingUsers.length === 0 && (
                      <p className="text-muted-foreground text-center py-4">No trending users yet</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
